import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createLaptop, updateLaptop } from '@/services/laptops';
import { fetchSuppliers } from '@/services/suppliers';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { Laptop } from '@/types/models';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { useAuth } from '@/context/AuthContext';
import { Save, Loader2, Sparkles, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { ExtractedLaptopSpecs, normalizeSpecValue, checkDuplicateSerial } from '@/services/aiExtraction';
import { LaptopSpecExtractorModal } from '@/features/ai/LaptopSpecExtractorModal';
import { Modal } from '@/components/common/Modal';

// Schema condition values match SQL: new | excellent | good | fair | poor
// Schema status values match SQL: in_stock | reserved | under_repair | returned | faulty
const laptopSchema = z.object({
  stock_id: z.string().max(50).optional().or(z.literal('')),
  brand: z.string().min(1, 'Brand is required').max(50),
  model: z.string().min(1, 'Model is required').max(100),
  serial_number: z.string().max(100).optional().or(z.literal('')),
  service_tag: z.string().max(100).optional().or(z.literal('')),
  cpu: z.string().max(100).optional().or(z.literal('')),
  ram: z.string().max(50).optional().or(z.literal('')),
  storage: z.string().max(50).optional().or(z.literal('')),
  gpu: z.string().max(100).optional().or(z.literal('')),
  screen_size: z.string().max(50).optional().or(z.literal('')),
  touchscreen: z.boolean().nullable().optional(),
  colour: z.string().max(50).optional().or(z.literal('')),
  keyboard_layout: z.string().max(50).optional().or(z.literal('')),
  condition: z.enum(['new', 'excellent', 'good', 'fair', 'poor']),
  battery_health: z.coerce
    .number()
    .min(0, 'Battery health must be 0-100')
    .max(100, 'Battery health must be 0-100')
    .optional()
    .or(z.literal('')),
  charger_included: z.boolean(),
  supplier_id: z.string().optional().or(z.literal('')),
  asking_price: z.coerce.number().min(0, 'Selling price cannot be negative'),
  minimum_price: z.union([z.literal(''), z.coerce.number().min(0)]).optional(),
  purchase_price: z.coerce.number().min(0).optional().or(z.literal('')),
  refurbishment_cost: z.coerce.number().min(0).optional().or(z.literal('')),
  status: z.enum(['in_stock', 'reserved', 'under_repair', 'returned', 'faulty', 'sold', 'scrapped']).optional(),
  warranty_months: z.coerce.number().min(0).max(60).optional().or(z.literal('')),
  is_public: z.boolean(),
  description: z.string().max(1000).optional().or(z.literal('')),
  notes: z.string().max(1000).optional().or(z.literal('')),
});

type LaptopFormData = z.infer<typeof laptopSchema>;

interface LaptopFormProps {
  laptop?: Laptop;
  initialSpecs?: ExtractedLaptopSpecs;
  onSuccess: () => void;
  onCancel: () => void;
}

export const LaptopForm: React.FC<LaptopFormProps> = ({
  laptop,
  initialSpecs,
  onSuccess,
  onCancel,
}) => {
  const { hasPermission } = useAuth();
  const canViewCosts = hasPermission('purchases');
  const queryClient = useQueryClient();
  const isEdit = !!laptop;
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [duplicateSerialWarning, setDuplicateSerialWarning] = useState<string | null>(null);
  const [showAiExtractor, setShowAiExtractor] = useState(false);
  const [aiAppliedFeedback, setAiAppliedFeedback] = useState<string | null>(null);

  const { data: suppliersData } = useQuery({
    queryKey: queryKeys.suppliers.list({ page: 0 }),
    queryFn: () => fetchSuppliers({ page: 0 }),
    enabled: canViewCosts,
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<LaptopFormData>({
    resolver: zodResolver(laptopSchema),
    defaultValues: {
      stock_id: laptop?.stock_id ?? '',
      brand: laptop?.brand ?? initialSpecs?.brand ?? '',
      model: laptop?.model ?? initialSpecs?.model ?? '',
      serial_number: laptop?.serial_number ?? initialSpecs?.serial_number ?? '',
      service_tag: laptop?.service_tag ?? initialSpecs?.service_tag ?? '',
      cpu: laptop?.cpu ?? initialSpecs?.cpu ?? '',
      ram: laptop?.ram ?? initialSpecs?.ram ?? '',
      storage: laptop?.storage ?? initialSpecs?.storage ?? '',
      gpu: laptop?.gpu ?? initialSpecs?.gpu ?? '',
      screen_size: laptop?.screen_size ?? initialSpecs?.screen_size ?? '',
      touchscreen: laptop?.touchscreen ?? (initialSpecs as any)?.touchscreen ?? null,
      colour: laptop?.colour ?? initialSpecs?.colour ?? '',
      keyboard_layout: laptop?.keyboard_layout ?? '',
      condition: (laptop?.condition as LaptopFormData['condition']) ?? 'good',
      battery_health: laptop?.battery_health ?? '',
      charger_included: laptop?.charger_included ?? false,
      supplier_id: laptop?.supplier_id ?? '',
      asking_price: laptop?.asking_price ?? 0,
      minimum_price: laptop?.minimum_price ?? '',
      purchase_price: laptop?.purchase_price ?? '',
      refurbishment_cost: laptop?.refurbishment_cost ?? '',
      status: (laptop?.status as LaptopFormData['status']) ?? 'in_stock',
      warranty_months: laptop?.warranty_months ?? 0,
      is_public: laptop?.is_public ?? false,
      description: laptop?.description ?? '',
      notes: laptop?.notes ?? '',
    },
  });

  const watchedSerial = watch('serial_number');

  // Asynchronously check duplicate serial numbers against the database
  useEffect(() => {
    let active = true;
    if (!watchedSerial || !watchedSerial.trim()) {
      setDuplicateSerialWarning(null);
      return;
    }

    const check = async () => {
      const res = await checkDuplicateSerial(watchedSerial, isEdit ? laptop?.id : undefined);
      if (active) {
        if (res.isDuplicate && res.existingLaptop) {
          setDuplicateSerialWarning(
            `Serial "${watchedSerial.trim()}" is already registered to stock item ${res.existingLaptop.stock_id} (${res.existingLaptop.brand} ${res.existingLaptop.model}).`
          );
        } else {
          setDuplicateSerialWarning(null);
        }
      }
    };

    const timer = setTimeout(check, 350);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [watchedSerial, isEdit, laptop?.id]);

  // ── Safe AI Extraction Application ──────────────────────────────────────────
  const handleAiConfirmed = (specs: ExtractedLaptopSpecs) => {
    const updatedFields: string[] = [];

    // Helper to safely populate non-empty values without erasing existing data
    const safePopulate = (
      fieldName: keyof LaptopFormData,
      extractedVal: string | null | undefined
    ) => {
      const normalized = normalizeSpecValue(extractedVal);
      if (normalized) {
        setValue(fieldName, normalized as never, { shouldDirty: true, shouldValidate: true });
        updatedFields.push(fieldName);
      }
    };

    safePopulate('brand', specs.brand);
    safePopulate('model', specs.model);
    safePopulate('serial_number', specs.serial_number);
    safePopulate('service_tag', specs.service_tag);
    safePopulate('cpu', specs.cpu);
    safePopulate('ram', specs.ram);
    safePopulate('storage', specs.storage);
    safePopulate('gpu', specs.gpu);
    safePopulate('screen_size', specs.screen_size);
    if ((specs as any).touchscreen !== undefined) {
      setValue('touchscreen', (specs as any).touchscreen, { shouldDirty: true });
      updatedFields.push('touchscreen');
    }
    safePopulate('colour', specs.colour);

    setShowAiExtractor(false);

    if (updatedFields.length > 0) {
      setAiAppliedFeedback(`Populated ${updatedFields.length} specs from AI draft. Review and save below.`);
    }
  };

  const mutation = useMutation({
    mutationFn: (data: LaptopFormData) => {
      const payload = {
        stock_id: isEdit ? data.stock_id : (data.stock_id?.trim() || undefined),
        brand: data.brand,
        model: data.model,
        serial_number: data.serial_number || null,
        service_tag: data.service_tag || null,
        cpu: data.cpu || null,
        ram: data.ram || null,
        storage: data.storage || null,
        gpu: data.gpu || null,
        screen_size: data.screen_size || null,
        touchscreen: data.touchscreen !== undefined ? data.touchscreen : null,
        colour: data.colour || null,
        keyboard_layout: data.keyboard_layout || null,
        condition: data.condition,
        battery_health: typeof data.battery_health === 'number' ? data.battery_health : null,
        charger_included: data.charger_included,
        supplier_id: data.supplier_id || null,
        asking_price: data.asking_price,
        minimum_price: canViewCosts ? (typeof data.minimum_price === 'number' ? data.minimum_price : null) : undefined,
        purchase_price: canViewCosts && typeof data.purchase_price === 'number' ? data.purchase_price : undefined,
        refurbishment_cost: canViewCosts && typeof data.refurbishment_cost === 'number' ? data.refurbishment_cost : undefined,
        status: isEdit && (laptop?.status === 'sold' || laptop?.status === 'scrapped') ? undefined : data.status,
        warranty_months: typeof data.warranty_months === 'number' ? data.warranty_months : 0,
        is_public: data.is_public,
        description: data.description || null,
        notes: data.notes || null,
      };

      return isEdit ? updateLaptop(laptop.id, payload) : createLaptop(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.laptops.all });
      onSuccess();
    },
    onError: (err: unknown) => {
      setSubmitError(parseDatabaseError(err));
    },
  });

  const onSubmit = (data: LaptopFormData) => {
    setSubmitError(null);
    mutation.mutate(data);
  };

  const inputClass = (hasError: boolean) =>
    `w-full px-3 py-2 bg-brand-bg border rounded-md text-sm text-brand-black placeholder-brand-muted focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent transition-all ${
      hasError ? 'border-brand-red focus:ring-brand-red' : 'border-brand-border'
    }`;

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 max-h-[75vh] overflow-y-auto pr-1" noValidate>
        {submitError && <ErrorAlert message={submitError} />}

        {/* Duplicate Serial Warning Banner */}
        {duplicateSerialWarning && (
          <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg text-xs text-amber-900 flex items-start space-x-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold">Duplicate Serial Warning</p>
              <p>{duplicateSerialWarning}</p>
            </div>
          </div>
        )}

        {/* AI Applied Feedback Banner */}
        {aiAppliedFeedback && (
          <div className="p-2.5 bg-green-50 border border-green-300 rounded-lg text-xs text-green-800 flex items-center justify-between">
            <div className="flex items-center space-x-1.5 font-semibold">
              <CheckCircle2 className="w-4 h-4 text-brand-green shrink-0" />
              <span>{aiAppliedFeedback}</span>
            </div>
            <button
              type="button"
              onClick={() => setAiAppliedFeedback(null)}
              className="text-green-700 hover:text-green-900 text-xs font-bold"
            >
              ✕
            </button>
          </div>
        )}

        {/* AI Spec Extract Helper Banner / Button */}
        <div className="flex items-center justify-between p-2.5 bg-gradient-to-r from-emerald-50 to-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <Sparkles className="w-4 h-4 text-brand-green shrink-0" />
            <span className="text-xs font-semibold text-green-900">
              Have a photo of the laptop sticker or spec label?
            </span>
          </div>
          <button
            type="button"
            onClick={() => setShowAiExtractor(true)}
            className="inline-flex items-center space-x-1.5 px-3 py-1 bg-white border border-brand-green/30 text-brand-green hover:bg-brand-green hover:text-white text-xs font-bold rounded shadow-sm transition-all"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>Scan Specs (AI)</span>
          </button>
        </div>

        {/* Stock ID, Brand, Model */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              Stock ID {isEdit ? <span className="text-brand-red">*</span> : <span className="text-brand-muted font-normal text-[10px] lowercase">(optional - auto-generated)</span>}
            </label>
            <input {...register('stock_id')} placeholder={isEdit ? 'e.g. LAP-000042' : 'Leave blank to auto-generate'} className={inputClass(!!errors.stock_id)} />
            {errors.stock_id && <p className="mt-1 text-xs text-brand-red">{errors.stock_id.message}</p>}
          </div>
          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              Brand <span className="text-brand-red">*</span>
            </label>
            <input {...register('brand')} placeholder="e.g. Dell" className={inputClass(!!errors.brand)} />
            {errors.brand && <p className="mt-1 text-xs text-brand-red">{errors.brand.message}</p>}
          </div>
          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              Model <span className="text-brand-red">*</span>
            </label>
            <input {...register('model')} placeholder="e.g. XPS 13 9310" className={inputClass(!!errors.model)} />
            {errors.model && <p className="mt-1 text-xs text-brand-red">{errors.model.message}</p>}
          </div>
        </div>

        {/* Serial & Service Tag */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              Serial Number
            </label>
            <input {...register('serial_number')} placeholder="SN123456789" className={inputClass(!!errors.serial_number)} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              Service Tag
            </label>
            <input {...register('service_tag')} placeholder="7XYZ123" className={inputClass(!!errors.service_tag)} />
          </div>
        </div>

        {/* Hardware Specs — text fields matching schema cpu, ram, storage */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              CPU
            </label>
            <input {...register('cpu')} placeholder="Core i7-1185G7" className={inputClass(!!errors.cpu)} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              RAM
            </label>
            <input {...register('ram')} placeholder="16GB DDR4" className={inputClass(!!errors.ram)} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              Storage
            </label>
            <input {...register('storage')} placeholder="512GB NVMe SSD" className={inputClass(!!errors.storage)} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              GPU
            </label>
            <input {...register('gpu')} placeholder="Intel Iris Xe" className={inputClass(!!errors.gpu)} />
          </div>
        </div>

        {/* Screen, Touchscreen, Colour, Keyboard, Condition */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              Screen Size
            </label>
            <input {...register('screen_size')} placeholder="13.3 inch FHD" className={inputClass(!!errors.screen_size)} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              Touchscreen
            </label>
            <select
              value={watch('touchscreen') === true ? 'true' : watch('touchscreen') === false ? 'false' : ''}
              onChange={(e) => setValue('touchscreen', e.target.value === 'true' ? true : e.target.value === 'false' ? false : null, { shouldDirty: true })}
              className={inputClass(false)}
            >
              <option value="">Unknown</option>
              <option value="true">Touchscreen</option>
              <option value="false">Non-Touch</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              Colour
            </label>
            <input {...register('colour')} placeholder="Silver / Black" className={inputClass(!!errors.colour)} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              Condition <span className="text-brand-red">*</span>
            </label>
            <select {...register('condition')} className={inputClass(!!errors.condition)}>
              <option value="new">New</option>
              <option value="excellent">Excellent</option>
              <option value="good">Good</option>
              <option value="fair">Fair</option>
              <option value="poor">Poor</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              Status <span className="text-brand-red">*</span>
            </label>
            {isEdit && (laptop?.status === 'sold' || laptop?.status === 'scrapped') ? (
              <div className="px-3 py-2 bg-slate-100 border border-slate-300 rounded-md text-xs font-bold text-slate-800 flex items-center justify-between">
                <span className="uppercase">{laptop.status.replace('_', ' ')}</span>
                <span className="text-[10px] text-slate-500 font-normal">Controlled via sale / removal</span>
              </div>
            ) : (
              <select {...register('status')} className={inputClass(!!errors.status)}>
                <option value="in_stock">In Stock</option>
                <option value="reserved">Reserved</option>
                <option value="under_repair">Under Repair</option>
                <option value="returned">Returned</option>
                <option value="faulty">Faulty</option>
              </select>
            )}
          </div>
        </div>

        {/* Pricing & Warranty */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              Selling Price (PKR) <span className="text-brand-red">*</span>
            </label>
            <input {...register('asking_price')} type="number" min="0" placeholder="0" className={inputClass(!!errors.asking_price)} />
            {errors.asking_price && <p className="mt-1 text-xs text-brand-red">{errors.asking_price.message}</p>}
          </div>
          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              Min Price (PKR)
            </label>
            <input {...register('minimum_price')} type="number" min="0" placeholder="0" className={inputClass(!!errors.minimum_price)} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              Warranty (Months)
            </label>
            <input {...register('warranty_months')} type="number" min="0" max="60" placeholder="0" className={inputClass(!!errors.warranty_months)} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              Battery Health (%)
            </label>
            <input {...register('battery_health')} type="number" min="0" max="100" placeholder="e.g. 88" className={inputClass(!!errors.battery_health)} />
          </div>
        </div>

        {/* Costs — Visible only to owner/manager */}
        {canViewCosts && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 bg-brand-bg rounded-lg border border-brand-border">
            <div>
              <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
                Purchase Price (PKR)
              </label>
              <input {...register('purchase_price')} type="number" min="0" placeholder="0" className={inputClass(!!errors.purchase_price)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
                Refurbishment Cost (PKR)
              </label>
              <input {...register('refurbishment_cost')} type="number" min="0" placeholder="0" className={inputClass(!!errors.refurbishment_cost)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
                Supplier
              </label>
              <select {...register('supplier_id')} className={inputClass(!!errors.supplier_id)}>
                <option value="">Select Supplier</option>
                {suppliersData?.data.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="flex items-center space-x-6 pt-1">
          <label className="flex items-center space-x-2 text-xs font-semibold text-brand-black cursor-pointer">
            <input type="checkbox" {...register('charger_included')} className="rounded text-brand-green focus:ring-brand-green" />
            <span>Charger Included</span>
          </label>
          <label className="flex items-center space-x-2 text-xs font-semibold text-brand-black cursor-pointer">
            <input type="checkbox" {...register('is_public')} className="rounded text-brand-green focus:ring-brand-green" />
            <span>Public Catalogue</span>
          </label>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
            Internal Notes
          </label>
          <textarea {...register('notes')} rows={2} placeholder="Condition notes, cosmetics, etc." className={`${inputClass(!!errors.notes)} resize-none`} />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 pt-3 border-t border-brand-border">
          <button type="button" onClick={onCancel} disabled={mutation.isPending} className="px-4 py-2 text-sm font-medium text-brand-muted hover:text-brand-black transition-colors disabled:opacity-50">
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending || (isEdit && !isDirty)}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-brand-green text-white text-sm font-semibold rounded-md hover:bg-brand-green-hover transition-colors disabled:opacity-50 shadow-sm"
          >
            {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span>{mutation.isPending ? 'Saving...' : isEdit ? 'Update Laptop' : 'Add Laptop'}</span>
          </button>
        </div>
      </form>

      {/* AI Spec Extractor Modal */}
      <Modal
        isOpen={showAiExtractor}
        onClose={() => setShowAiExtractor(false)}
        title="Extract Laptop Specs with AI"
        maxWidth="md"
      >
        <LaptopSpecExtractorModal
          onConfirm={handleAiConfirmed}
          onCancel={() => setShowAiExtractor(false)}
        />
      </Modal>
    </>
  );
};
